import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import { ViewedExecuteSopsConfig } from "./ViewedExecuteSopsConfig";
import { useTranslation } from "react-i18next";
import {
  getData,
  postDataWithBody,
  putDataWithBody,
  getDataById,
} from "../../../../../Services/Services";

import {
  getTaskConfigsURL,
  createTaskURL,
  findTaskById,
  updateTaskURL,
  incidentList,
  sopList,
  SopTaskTableData,
  sopResourceList,
  sopGetIncident,
  SopUserData,
} from "../../../../../Services/EndPoints";
import Loader from "react-spinner-loader";
import { downloadFile } from "../../../../../Components/UiComponents/utils";
import { PaperClipIcon } from "../../../../../Components/IconComponents";
import ViewedExecuteSopsExpandableTable from "./ViewedExecuteSopsExpandableTable";
import ViewedSopInstanceMap from "./ViewedSopInstanceMap";

/**
 * ViewedExecuteSopsForm Component
 * 
 * A comprehensive form component for viewing Standard Operating Procedures (SOPs).
 */
const ViewedExecuteSopsForm = () => {
  // ===== HOOKS AND SETUP =====
  
  const { t } = useTranslation(); // Internationalization hook
  const { state } = useLocation(); // Get navigation state data
  const navigate = useNavigate(); // Navigation hook
  
  // ===== STATE MANAGEMENT =====
  
  // Task and user data
  const [userTaskData, setUserTaskData] = useState([]); // Array of user tasks
  const [rowCount, setRowCount] = useState(0); // Total number of tasks
  const [viewTaskDetails, setViewTaskDetails] = useState(null); // Selected task details
  
  // Loading and form states
  const [isLoading, setIsLoading] = useState(false); // General loading state
  const [isSubmitting, setIsSubmitting] = useState(false); // Form submission state
  
  // Configuration data
  const [sopFormConfig, setSopFormConfig] = useState(ViewedExecuteSopsConfig); // Form configuration
  const [taskConfigs, setTaskConfigs] = useState([]); // Task configuration options
  const [availableRoles, setAvailableRoles] = useState([]); // Available SOP roles/types
  
  // ===== EXTRACT ROUTE PARAMETERS =====
  
  // Extract data passed through navigation state
  const taskId = state?.data?.id; // Task ID for editing existing tasks
  const mediaFilePath = state?.data?.media_file_path; // Path to media files
  
  // ===== FORM SETUP =====
  
  /**
   * React Hook Form setup with validation
   * Provides form state management, validation, and submission handling
   */
  const {
    register, // Register form fields
    handleSubmit, // Handle form submission
    watch, // Watch form field changes
    setValue, // Programmatically set field values
    reset, // Reset form to default/initial values
    control, // Form control for custom components
    formState: { errors }, // Form validation errors
  } = useForm({
    resolver: yupResolver(), // Yup validation resolver
    mode: "all", // Validate on all interactions
  });

  // ===== FORM WATCHERS =====
  
  // Watch for changes in critical dropdown fields
  const selectedSopId = watch("task_name"); // Currently selected SOP ID
  const selectedIncidentId = watch("incident_type_id"); // Currently selected incident ID

  // ===== EFFECT HOOKS =====
  
  /**
   * Component initialization effect
   * Loads initial data when component mounts
   */
  useEffect(() => {
    loadInitialData();
    fetchSopUserData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * SOP selection effect
   * Triggered when a SOP is selected from dropdown
   * Loads related tasks and resources, and auto-generates instance name
   */
  useEffect(() => {
    if (selectedSopId) {
      fetchTaskList(); // Load tasks for selected SOP
      fetchSopResourceList(); // Load resources for selected SOP

      // Auto-generate instance name with timestamp
      const selectedSop = availableRoles.find(
        (sop) => sop.id === parseInt(selectedSopId)
      );
      
      if (selectedSop) {
        const now = new Date();

        // Format date as DD:MM:YYYY
        const day = String(now.getDate()).padStart(2, "0");
        const month = String(now.getMonth() + 1).padStart(2, "0");
        const year = now.getFullYear();
        const formattedDate = `${day}:${month}:${year}`;

        // Format time in 24-hour format (HH:mm)
        const hours = String(now.getHours()).padStart(2, "0");
        const minutes = String(now.getMinutes()).padStart(2, "0");
        const formattedTime = `${hours}:${minutes}`;

        // Combine SOP name with date and time for unique instance naming
        const nameValue = `${selectedSop.name}_${formattedDate} ${formattedTime}`;

        // Set the generated name in the form
        setValue("assign_to_id", nameValue, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSopId]);

  /**
   * Incident selection effect
   * Triggered when an incident is selected from dropdown
   * Loads incident-specific data
   */
  useEffect(() => {
    if (selectedIncidentId) {
      fetchSopIncident();
    }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIncidentId]);

  // ===== DATA FETCHING FUNCTIONS =====
  
  /**
   * Fetches the list of tasks for the selected SOP
   * Sorts tasks by creation date (most recent first)
   */
  const fetchTaskList = async () => {
    setIsLoading(true);
    try {
      const response = await getDataById(SopTaskTableData, selectedSopId);
      
      // Sort tasks by creation date (newest first)
      const sortedTasks = response
        ? response.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn))
        : [];

      setUserTaskData(sortedTasks);
      setRowCount(sortedTasks.length);
    } catch (error) {
      console.error("Error fetching task list:", error);
      toast.error(t("fetch_error_message"), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Fetches resource list for the selected SOP
   * Resources may include equipment, personnel, or other assets needed
   */
  const fetchSopResourceList = async () => {
    setIsLoading(true);
    try {
      const response = await getDataById(sopResourceList, selectedSopId);
      // TODO: Process and store resource data as needed
    } catch (error) {
      console.error("Error fetching SOP resources:", error);
      toast.error(t("fetch_error_message"), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Fetches incident data for the selected incident type
   * Loads incident-specific information for SOP execution context
   */
  const fetchSopIncident = async () => {
    setIsLoading(true);
    try {
      const response = await getDataById(sopGetIncident, selectedIncidentId);
      // TODO: Process and store incident data as needed
    } catch (error) {
      toast.error(t("fetch_error_message"), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Fetches user data related to SOP execution
   * May include assigned users, roles, or permissions
   */
  const fetchSopUserData = async () => {
    setIsLoading(true);
    try {
      const response = await getDataById(SopUserData, selectedSopId);
      // TODO: Process and store user data as needed
    } catch (error) {
      console.error("Error fetching SOP user data:", error);
      toast.error(t("fetch_error_message"), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Loads all initial data required for the component
   * Orchestrates multiple API calls in sequence
   */
  const loadInitialData = async () => {
    setIsLoading(true);
    
    // Load configuration and reference data
    await fetchTaskConfigs(); // Load task configuration options
    await fetchIncidentTypes(); // Load available incident types
    await fetchSopList(); // Load available SOPs
    await fetchViewData(); // Load view-specific data
    
    setIsLoading(false);

    // If editing existing task, populate form with existing data
    if (taskId) {
      populateFormForEdit(taskId);
    }
  };

  /**
   * Fetches available incident types and updates form configuration
   * Formats incident types for dropdown selection
   */
  const fetchIncidentTypes = async () => {
    const incidentTypes = await getData(incidentList);
    
    // Format for dropdown component
    const formattedTypes = incidentTypes.map((type) => ({
      label: type.name,
      value: type.id,
    }));

    // Update form configuration with incident type options
    updateFormConfig(0, 0, formattedTypes);
  };

  /**
   * Fetches available SOPs and updates form configuration
   * Formats SOPs for dropdown selection
   */
  const fetchSopList = async () => {
    const sops = await getData(sopList);
    setAvailableRoles(sops); // Store for later use in name generation

    // Format for dropdown component
    const formattedSops = sops.map((sop) => ({
      label: sop.name,
      value: sop.id,
    }));

    // Update form configuration with SOP options
    updateFormConfig(0, 1, formattedSops);
  };

  /**
   * Updates form configuration with new dropdown options
   */
  const updateFormConfig = (configIndex, fieldIndex, options) => {
    const updatedConfig = [...sopFormConfig];
    updatedConfig[configIndex].fields[fieldIndex].options = options;
    setSopFormConfig(updatedConfig);
  };

  /**
   * Fetches task configuration data including priorities, categories, statuses, and types
   * Loads lookup data for various task-related dropdowns
   */
  const fetchTaskConfigs = async () => {
    const response = await postDataWithBody(
      getTaskConfigsURL,
      ["TSK_PRIOR", "INCDNT_CAT", "TSK_STAT", "TSK_TYP"], // Configuration types to fetch
      {}
    );
    setTaskConfigs(response);
  };

  /**
   * Fetches view-specific data and handles edit mode initialization
   */
  const fetchViewData = async () => {
    
    // If editing existing task, populate form
    if (taskId) {
      populateFormForEdit(taskId);
    }
  };

  /**
   * Populates form fields for editing an existing task
   */
  const populateFormForEdit = async (id) => {
    const response = await postDataWithBody(findTaskById, [id], {});
    const taskData = response[0];
    
    setViewTaskDetails(taskData); // Store task details for reference
    reset(taskData); // Populate form with existing data
  };

  // ===== EVENT HANDLERS =====
  
  /**
   * Handles navigation to SOP instance page
   * Navigates to the SOP instance management interface
   */
  const handleclick = () => {
    navigate("../sops-instance");
  }

  /**
   * Generates the main form fields and SOP instance information display
   */
  const generateFormFields = () => {
    return (
      <>
        <div className="form-body">
          <div className="registration__form--body">
            {/* SOP Instance Summary Table */}
            <div className="sop-instance-table">
              {/* Table Headers */}
              <div class="row">
                <div class="col-sm-3">
                  <div className="sop_instance_info">ID</div>
                </div>
                <div class="col-sm-3">
                  <div className="sop_instance_info">Instance Name</div>
                </div>
                <div class="col-sm-3">
                  <div className="sop_instance_info">Status</div>
                </div>
              </div>
              
              {/* Table Data Row */}
              <div class="row">
                <div class="col-sm-3">
                  <div>258</div> {/* Static SOP Instance ID */}
                </div>
                <div class="col-sm-3">
                  <div>Gas Leakage Overview name SOP 08:01:2025 16:24</div> {/* Instance name with timestamp */}
                </div>
                <div class="col-sm-3">
                  <div>Started</div> {/* Current execution status */}
                </div>
                <div class="col-sm-3">
                  {/* Action buttons for SOP management */}
                  <button class="btn btn-deploy" onClick={handleclick}>
                    Assign User Task
                  </button>
                  <button class="btn btn-close-sop">
                    Close SOP
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  /**
   * Generates tab content for the form
   * Processes tab configuration and assigns form field components
   */
  const generateTabsContent = (tabData) => {
    tabData?.forEach((tab) => {
      // Generate form fields for each tab
      tab.component = generateFormFields(
        tab?.fields,
        register,
        errors,
        control
      );
    });
    return tabData;
  };

  /**
   * Handles form submission for creating or updating SOP tasks
   * Processes form data, handles file uploads, and submits to appropriate API endpoint
   */
  const handleFormSubmit = async (data, e) => {
    setIsLoading(true);
    setIsSubmitting(true);
    e.preventDefault();

    // Create FormData object for file upload support
    const formData = new FormData();
    
    // Process all form fields
    Object.entries(data).forEach(([key, value]) => {
      if (key === "task_media_file" && value?.length > 0) {
        // Handle multiple file uploads
        Array.from(value).forEach((file) => formData.append(key, file));
      } else {
        // Handle regular form fields
        formData.append(key, value);
      }
    });

    // Add additional required fields
    formData.append("status", true); // Set task as active
    if (taskId) formData.append("id", taskId); // Include task ID for updates

    try {
      const headers = { "Content-Type": "multipart/form-data" };
      
      // Choose API endpoint based on operation type (create vs update)
      const response = taskId
        ? await putDataWithBody(updateTaskURL, formData, headers) // Update existing task
        : await postDataWithBody(createTaskURL, formData, headers); // Create new task

      // Handle API response
      if (response.status === "success") {
        toast.success(response.msg, {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
        navigate("/planning/view-task"); // Navigate to task list view
      } else {
        toast.error(response.msg, {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error(t("submission_error"), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  // ===== TABLE CONFIGURATION =====
  
  /**
   * Table column configuration for the expandable task table
   * Defines headers, data accessors, and cell renderers
   */
  const tableColumns = React.useMemo(
    () => [
      {
        Header: t("viewedexecutesopstableheaders.id"), // Task ID column
        accessor: "name",
        disableSortBy: true,
      },
      {
        Header: t("viewedexecutesopstableheaders.name"), // Task name column
        accessor: "task_type",
        disableSortBy: true,
        // Custom cell renderer for task type
        Cell: ({ value }) => (value === 0 ? "Activity" : "Notification"),
      },
      {
        Header: t("viewedexecutesopstableheaders.status"), // Task status column
        accessor: "incidenttype",
        disableSortBy: true,
      },
      {
        Header: t("viewedexecutesopstableheaders.quantity"), // Quantity/assignment column
        accessor: "assignedto",
        disableSortBy: true,
      },
      {
        Header: t("viewedexecutesopstableheaders.action"), // Action buttons column
        accessor: "",
        disableSortBy: true,
      },
      {
        Header: t("viewedexecutesopstableheaders.locateonmap"), // Map location column
        accessor: "",
        disableSortBy: true,
      },
    ],
    [t] // Re-compute when translation changes
  );

  // ===== FILE DOWNLOAD CONFIGURATION =====
  
  /**
   * Download URL for task attachments
   * Constructs URL for downloading zipped task attachments
   */
  const downloadAttachmentURL = `${process.env.REACT_APP_APP_SERVER_ADDRESS}/${process.env.REACT_APP_CORE_CONTEXT_PATH_NAME}/download/download_attachments/?directoryPath=${mediaFilePath}&fileName=Tasks.zip`;

  // ===== PREPARE RENDER DATA =====
  
  // Generate tab content with form fields
  const tabsContent = generateTabsContent(sopFormConfig);

  // ===== RENDER =====
  
  return (
    <>
      {/* Main Form Section */}
      <div className="mt-4">
        {/* Loading spinner overlay */}
        <Loader
          show={isLoading}
          type="body"
          stack="vertical"
          message="Loading Data"
        />
        
        <div className="form-body">
          <div className="registration__form--body">
            {/* Main form with submission handler */}
            <form
              onSubmit={handleSubmit(handleFormSubmit)}
              name="CreateTaskRegForm"
            >
              <div>
                {/* Render form tabs and their content */}
                {tabsContent?.map((tab) => (
                  <div key={tab.id}>
                   <div>{tab.component}</div>
                  </div>
                ))}
              </div>

              {/* File attachment download section (only shown if files exist) */}
              {taskId && mediaFilePath && (
                <div className="multi_file_uploaded cursor-pointer">
                  <div
                    onClick={() =>
                      downloadFile(
                        downloadAttachmentURL,
                        `Tasks${Date.now()}.zip` // Generate unique filename with timestamp
                      )
                    }
                  >
                    <PaperClipIcon className="cursor-pointer" /> Attached files
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Map and Table Section */}
      <div>
        {/* Loading spinner for map/table operations */}
        <Loader show={isLoading} type="body" message="Loading Data" />
        
        {/* Interactive map component for SOP visualization */}
        <div className="ViewedSopInstanceMap">
          <ViewedSopInstanceMap />
        </div>

        {/* Expandable table for task management */}
        <ViewedExecuteSopsExpandableTable
          data={userTaskData} // Task data to display
          columns={tableColumns} // Column configuration
          numberOfRows={7} // Rows per page
          showExport={false} // Disable export functionality
          showFilter={false} // Disable filtering
          showReset={false} // Disable reset functionality
          onExpand={false} // Disable row expansion
          rowCount={rowCount} // Total number of rows
          headerTableHeadStyle={{ background: "#1a7a73", color: "#FFFFFF" }} // Header styling
          tableStyle={{
            border: "1px solid #f0f0f0",
            borderRadius: "12px",
          }} // Table styling
        />
      </div>
    </>
  );
};

export default ViewedExecuteSopsForm;